import { formateInternationalPhoneNumber } from "../../util/phone";
import type { FunctionComponent } from "react";
import styled from "styled-components/macro";
import type { IAddress, LeadAddress, User } from "../../types/types";
import { LocationIcon, UserIcon } from "../Icons/Icons";
import { InfoBlockSmall } from "../InfoBlocks/InfoBlocks";
import { EditButton } from "../Buttons/Buttons";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../Auth";
import ReactTooltip from "react-tooltip";
import { EmailBlock } from "../../pages/SharedPages/CustomerDetailPage/shared";
import { TextMdSemibold } from "../Typography/TypographyV2";

const LocationsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  width: 100%;
`;

const LocationCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  width: 100%;
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_standard")};
`;

const LocationCardHeader = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const LocationCardBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
`;

export const MultiLineBlock = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.regular};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  display: inline-block;
`;

const PointOfContactEntry = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};

  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.resolve("spacing_lg")};
  }
`;

export function AddressBlock({
  address,
  is_transaction = false,
}: {
  address?: Partial<IAddress>;
  is_transaction?: boolean;
}) {
  const { t } = useTranslation();
  const { roleIsSomeKindOfBuyer } = useAuthContext();
  return address ? (
    <MultiLineBlock>
      {address?.company_name && <div>{address.company_name}</div>}
      <div>
        {address?.address1} {address?.address2}
      </div>
      <div>
        {address?.city ? `${address?.city}, ` : ""}{" "}
        {address?.county ? `${address.county} ` : ""}
        {address?.state_display_name ? address?.state_display_name + " " : "--"}
        {address?.postal_code ?? ""}
      </div>
      <div>
        {address?.country && address?.country !== "" ? address?.country : "--"}
      </div>
      {address?.tax_id && address?.tax_id !== "" && (
        <div>
          {t("Tax ID")}: {address?.tax_id}
        </div>
      )}
      {!roleIsSomeKindOfBuyer &&
        !is_transaction &&
        address.type === "Warehouse" &&
        address?.crm_id &&
        address?.crm_id !== "" && (
          <div>
            {t("CRM ID")}: {address?.crm_id ?? "--"}
          </div>
        )}
      {address?.external_identity && (
        <div>
          {t("Location ID")}: {address?.external_identity.external_id}
        </div>
      )}
    </MultiLineBlock>
  ) : (
    <MultiLineBlock>{`--`}</MultiLineBlock>
  );
}

export function ContactBlock({ address }: { address: IAddress }) {
  return (
    <MultiLineBlock>
      {address.points_of_contact?.map((poc) => (
        <PointOfContactEntry key={poc.id}>
          <UserIcon width={16} />
          <div>
            <div>
              <TextMdSemibold>{poc.contact_name}</TextMdSemibold>
            </div>
            <EmailBlock email={poc.email_address} />
            <div>
              <a href={`tel:${poc.phone_number}`}>
                {formateInternationalPhoneNumber(poc.phone_number)}
              </a>
            </div>
          </div>
        </PointOfContactEntry>
      ))}
    </MultiLineBlock>
  );
}

export type LeadAddressWithContact = {
  company_name: string;
  email_address: string;
  first_name: string;
  last_name: string;
  phone_number: string;
} & Partial<LeadAddress>;

export function ContactBlockForLead({
  address,
}: {
  address: LeadAddressWithContact;
}) {
  const maxEmailLength = 38;
  const isEmailTooLong = address?.email_address?.length > maxEmailLength;

  return (
    <MultiLineBlock>
      <div>{address?.first_name + " " + address?.last_name}</div>
      <div>{address.company_name}</div>
      <div>
        {isEmailTooLong ? (
          <>
            <span data-tip={address.email_address}>
              {address.email_address.slice(0, maxEmailLength)}...
            </span>
            <ReactTooltip effect="solid" />
          </>
        ) : (
          address.email_address
        )}
      </div>
      <div>
        <a href={`tel:${address.phone_number}`}>
          {formateInternationalPhoneNumber(address.phone_number)}
        </a>
      </div>
    </MultiLineBlock>
  );
}

/**
 * Given a user object, returns a contact block showing that user's name,
 * email, and phone number. If the user is not defined, returns placeholders.
 */
export function ContactBlockForUser({ user }: { user: User | undefined }) {
  const fullname =
    !user || (!user.firstname && !user.lastname)
      ? "--"
      : `${user.firstname || "--"} ${user.lastname || "--"}`;

  return (
    <MultiLineBlock>
      <div>{fullname}</div>
      <EmailBlock email={user?.email_address || "--"} />
      {user?.phone_number && (
        <div>
          <a href={`tel:${user.phone_number}`}>
            {formateInternationalPhoneNumber(user.phone_number)}
          </a>
        </div>
      )}
    </MultiLineBlock>
  );
}

interface LocationsListProps {
  addresses: IAddress[];
  onEditAddress?: (address: IAddress) => void;
  onDeleteAddress?: (id: string) => void;
}

/**
 * A list of locations (which include address and contact info). For example,
 * used for "Company Info" pages.
 */
export const LocationsList: FunctionComponent<LocationsListProps> = ({
  addresses,
  onEditAddress,
}) => {
  const { t } = useTranslation();
  return (
    <LocationsStack>
      {addresses
        .filter((address) => address.type === "Warehouse")
        .map((address, index) => {
          return (
            <LocationCard key={address.id + index}>
              {onEditAddress && (
                <LocationCardHeader>
                  <EditButton
                    onClick={() => onEditAddress(address)}
                    testid={`location-edit-button-${address.id}`}
                  />
                </LocationCardHeader>
              )}
              <LocationCardBody>
                <InfoBlockSmall
                  header={t("Address")}
                  content={<AddressBlock address={address} />}
                  icon={<LocationIcon width={16} />}
                />
                <InfoBlockSmall
                  header={t("Point of Contact")}
                  content={<ContactBlock address={address} />}
                />
              </LocationCardBody>
              {/* {onDeleteAddress && (
                //  ID is for testing deletion after creation.
                <DeleteButton
                  id={address.id}
                  onClick={() => onDeleteAddress(address.id)}
                  testid={`location-delete-button-${address.email_address}`}
                />
              )} */}
            </LocationCard>
          );
        })}
    </LocationsStack>
  );
};
